import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogIn, LogOut, Users, User, Wrench, Package, QrCode, Bell, AlertTriangle, Clock } from "lucide-react";
import { useVisitors } from "@/hooks/useVisitors";
import { useAuth } from "@/hooks/useAuth";
import { useCondominium } from "@/hooks/useCondominium";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { isToday, isYesterday, format, differenceInMinutes, getHours } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const DashboardAcessos = () => {
  const { profile } = useAuth();
  const { data: condominium } = useCondominium();
  const { data: visitors = [] } = useVisitors();
  const { data: announcements = [] } = useAnnouncements();
  const navigate = useNavigate();
  const [chartPeriod, setChartPeriod] = useState<"hoje" | "ontem">("hoje");

  const todayVisitors = useMemo(() => visitors.filter(v => v.created_at && isToday(new Date(v.created_at))), [visitors]);
  const yesterdayVisitors = useMemo(() => visitors.filter(v => v.created_at && isYesterday(new Date(v.created_at))), [visitors]);
  const activeVisitors = useMemo(() => todayVisitors.filter(v => !v.exit_time), [todayVisitors]);
  const exitedToday = useMemo(() => todayVisitors.filter(v => !!v.exit_time), [todayVisitors]);
  const pendingExit = useMemo(() => todayVisitors.filter(v => !v.exit_time && v.status !== "pending"), [todayVisitors]);

  // Chart data: visitors grouped by hour
  const chartData = useMemo(() => {
    const source = chartPeriod === "hoje" ? todayVisitors : yesterdayVisitors;
    const hours: { hora: string; entradas: number }[] = [];
    for (let h = 6; h <= 22; h++) {
      const label = `${String(h).padStart(2, "0")}h`;
      const count = source.filter(v => v.created_at && getHours(new Date(v.created_at)) === h).length;
      hours.push({ hora: label, entradas: count });
    }
    return hours;
  }, [chartPeriod, todayVisitors, yesterdayVisitors]);

  // Recent visitors (last 60 min or last 10)
  const recentVisitors = useMemo(() => todayVisitors.slice(0, 10), [todayVisitors]);

  const getInitials = (name: string) => {
    const parts = name.split(" ");
    return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
  };

  const getTypeBadge = (type: string | null) => {
    switch (type?.toLowerCase()) {
      case "servico": return <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">SERVIÇO</Badge>;
      case "familiar": return <Badge className="bg-pink-100 text-pink-800 border-pink-200 hover:bg-pink-100">FAMILIAR</Badge>;
      case "entrega": return <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">ENTREGA</Badge>;
      default: return <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">CASUAL</Badge>;
    }
  };

  const getStatusBadge = (status: string | null) => {
    if (status === "authorized" || status === "completed") {
      return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Autorizado</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">Em Espera</Badge>;
  };

  const avatarColors = ["bg-blue-500", "bg-violet-500", "bg-emerald-500", "bg-rose-500", "bg-amber-500", "bg-cyan-500"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{condominium?.name || "Portaria Principal"}</h1>
        <p className="text-sm text-muted-foreground">
          Bem-vindo, {profile?.full_name?.split(" ")[0] || "Operador"} • {format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR })}
        </p>
      </div>

      {/* Main Grid: 2/3 content + 1/3 sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950">
                    <LogIn className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground">{todayVisitors.length}</p>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Entradas</p>
                    <p className="text-xs text-muted-foreground">Nas últimas 24h</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950">
                    <LogOut className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground">{exitedToday.length}</p>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Saídas</p>
                    <p className="text-xs text-muted-foreground">Pendentes de baixa: {pendingExit.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-950">
                    <Users className="h-6 w-6 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground">{activeVisitors.length}</p>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">No Prédio</p>
                    <p className="text-xs text-muted-foreground">Visitantes ativos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Fluxo de Movimentação</CardTitle>
              <div className="flex gap-1">
                <Button
                  variant={chartPeriod === "hoje" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setChartPeriod("hoje")}
                >
                  HOJE
                </Button>
                <Button
                  variant={chartPeriod === "ontem" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setChartPeriod("ontem")}
                >
                  ONTEM
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="hora" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="entradas" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Recent Visitors Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Últimos Visitantes</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {recentVisitors.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhum visitante hoje</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>VISITANTE</TableHead>
                      <TableHead>DESTINO</TableHead>
                      <TableHead>ENTRADA</TableHead>
                      <TableHead>TIPO</TableHead>
                      <TableHead>STATUS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentVisitors.map((v, i) => {
                      const entryTime = v.entry_time ? new Date(v.entry_time) : v.created_at ? new Date(v.created_at) : new Date();
                      const minutesAgo = differenceInMinutes(new Date(), entryTime);
                      return (
                        <TableRow key={v.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className={`${avatarColors[i % avatarColors.length]} text-white text-xs font-bold`}>
                                  {getInitials(v.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">{v.name}</p>
                                <p className="text-xs text-muted-foreground">{v.document || "—"}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{v.notes || "—"}</TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{format(entryTime, "HH:mm")}</p>
                              <p className="text-xs text-muted-foreground">Há {minutesAgo} min</p>
                            </div>
                          </TableCell>
                          <TableCell>{getTypeBadge(v.visitor_type)}</TableCell>
                          <TableCell>{getStatusBadge(v.status)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column (1/3) */}
        <div className="space-y-6">
          {/* Acesso Rápido */}
          <Card className="bg-gray-900 dark:bg-gray-950 border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-gray-300">Acesso Rápido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 border-gray-700 bg-gray-800/50 text-gray-200 hover:bg-gray-700 hover:text-white"
                  onClick={() => navigate("/acessos")}
                >
                  <User className="h-5 w-5" />
                  <span className="text-xs">Visitante</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 border-gray-700 bg-gray-800/50 text-gray-200 hover:bg-gray-700 hover:text-white"
                  onClick={() => navigate("/acessos")}
                >
                  <Wrench className="h-5 w-5" />
                  <span className="text-xs">Serviço</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 border-gray-700 bg-gray-800/50 text-gray-200 hover:bg-gray-700 hover:text-white"
                  onClick={() => navigate("/acessos")}
                >
                  <Package className="h-5 w-5" />
                  <span className="text-xs">Entrega</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 border-gray-700 bg-gray-800/50 text-gray-200 hover:bg-gray-700 hover:text-white"
                  onClick={() => navigate("/acessos")}
                >
                  <QrCode className="h-5 w-5" />
                  <span className="text-xs">QR Code</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Avisos */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold">Avisos</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {announcements.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum aviso no momento</p>
              ) : (
                announcements.slice(0, 4).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 pb-3 border-b last:border-0 last:pb-0">
                    <div className={`p-1.5 rounded-lg mt-0.5 ${a.is_important ? "bg-red-50 dark:bg-red-950" : "bg-blue-50 dark:bg-blue-950"}`}>
                      {a.is_important ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                      ) : (
                        <Bell className="h-3.5 w-3.5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{a.content}</p>
                      {a.created_at && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(a.created_at), "dd/MM HH:mm")}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardAcessos;
